import { autoinject, bindable, observable } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { PubSub } from 'lib/event/PubSub';
import { ListSetting } from './list-settings';
import { FilterManager } from 'lib/tables/FilterManager';

type SearchTextChangedEvent = {
  context: string;
  searchText: string | undefined;
};

type ColumnVisibilityChangedEvent = {
  context: string;
  fields: [];
};

type ResetFiltersEvent = {
  context: string;
};

type ListSettingsChangedEvent = {
  context: string;
  settings: ListSetting;
};

type FilterResetEvent = {
  context: string;
  name: string;
};

const FilterSectionEventKey = {
  listSettingsChanged: 'filter-section:list-settings-changed',
  columnVisibilityChanged: 'filter-section:column-visibility-changed',
  searchTextChanged: 'filter-section:search-text-changed',
  filterShowHideChanged: 'filter-section:filter-show-hide-changed',
  resetFilters: 'filter-section:reset-filters',
  resetFilter: 'filter-section:reset-filter',
} as const;

export type FilterSectionEvents = {
  [FilterSectionEventKey.searchTextChanged]: SearchTextChangedEvent;
  [FilterSectionEventKey.resetFilters]: ResetFiltersEvent;
  [FilterSectionEventKey.columnVisibilityChanged]: ColumnVisibilityChangedEvent;
  [FilterSectionEventKey.listSettingsChanged]: ListSettingsChangedEvent;
  [FilterSectionEventKey.resetFilter]: FilterResetEvent;
};

// events
// -- list settings changed

@autoinject
export class FilterSection {
  // The context name of the filter section
  // Used in the published event
  @bindable
  protected context?: string;

  @bindable
  protected filterManager?: FilterManager;

  @observable
  protected searchText: string | undefined = undefined;
  searchTextChanged(newValue: string) {
    this.pubsub.publish?.(FilterSectionEventKey.searchTextChanged, {
      context: this.filterManager?.context || this.context,
      searchText: newValue,
    });
  }

  @bindable
  protected filters: { [key: string]: unknown } = undefined;

  @bindable
  protected fields: { checked: boolean; title: string; disabled: boolean; selected: boolean }[] = [];

  @bindable
  protected hideSettings: boolean;

  @bindable
  protected debounceTime?: number;

  constructor(private pubsub: PubSub, private eventAggregator: EventAggregator) {}

  protected closeFieldSelector() {
    this.eventAggregator.publish('dropdownClose', 1);
  }

  protected onClearFilters() {
    this.searchText = '';
    this.pubsub.publish?.(FilterSectionEventKey.resetFilters, {
      context: this.filterManager?.context || this.context,
    });
  }

  protected onColumnVisibilityChange() {
    this.pubsub.publish?.(FilterSectionEventKey.columnVisibilityChanged, {
      context: this.filterManager?.context || this.context,
      fields: JSON.parse(JSON.stringify(this.fields)),
    });
  }

  protected onListSettingsChanged(pageSize: number, tableViewType: number, tableViewVerticalLines: boolean, tableMargin: number) {
    this.pubsub.publish?.(FilterSectionEventKey.listSettingsChanged, {
      context: this.filterManager?.context || this.context,
      settings: {
        pageSize,
        tableViewType,
        tableViewVerticalLines,
        tableMargin,
      },
    });
  }
}
